import { apiClient } from '../api-client';
import type {
  FormRecordDto,
  CreateFormDto,
  UpdateFormDto,
  ReopenFormDto,
} from '../../dtos/patientmr/forms.dto';
import type { TherapyDiscipline } from '../../dtos/patientmr/assessments/therapy-tests.dto';

const toBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      const result = reader.result as string;
      resolve(result.split(',')[1]);
    };
    reader.onerror = reject;
  });

export class FormService {
  static async list(patientId: string): Promise<FormRecordDto[]> {
    const res = await apiClient.get<{ items: FormRecordDto[] }>(`/patientmr/${patientId}/forms`);
    if (res.success && res.data) {
      return res.data.items ?? [];
    }
    throw new Error(res.error?.message || 'Failed to fetch forms');
  }

  static async get(patientId: string, formId: string): Promise<FormRecordDto> {
    const res = await apiClient.get<FormRecordDto>(`/patientmr/${patientId}/forms/${formId}`);
    if (res.success && res.data) {
      return res.data;
    }
    throw new Error(res.error?.message || 'Failed to fetch form');
  }

  static async create(patientId: string, dto: CreateFormDto): Promise<FormRecordDto> {
    const res = await apiClient.post<FormRecordDto>(`/patientmr/${patientId}/forms`, dto);
    if (res.success && res.data) {
      return res.data;
    }
    throw new Error(res.error?.message || 'Failed to create form');
  }

  static async update(patientId: string, formId: string, dto: UpdateFormDto): Promise<FormRecordDto> {
    const res = await apiClient.put<FormRecordDto>(`/patientmr/${patientId}/forms/${formId}`, dto);
    if (res.success && res.data) {
      return res.data;
    }
    throw new Error(res.error?.message || 'Failed to update form');
  }

  static async delete(patientId: string, formId: string): Promise<void> {
    const res = await apiClient.delete(`/patientmr/${patientId}/forms/${formId}`);
    if (!res.success) {
      throw new Error(res.error?.message || 'Failed to delete form');
    }
  }

  static async submit(patientId: string, formId: string): Promise<FormRecordDto> {
    const res = await apiClient.put<FormRecordDto>(`/patientmr/${patientId}/forms/${formId}/submit`, {});
    if (res.success && res.data) {
      return res.data;
    }
    throw new Error(res.error?.message || 'Failed to submit form');
  }

  static async startQA(patientId: string, formId: string): Promise<FormRecordDto> {
    const res = await apiClient.put<FormRecordDto>(`/patientmr/${patientId}/forms/${formId}/startqa`, {});
    if (res.success && res.data) {
      return res.data;
    }
    throw new Error(res.error?.message || 'Failed to start QA');
  }

  static async finalize(patientId: string, formId: string): Promise<FormRecordDto> {
    const res = await apiClient.put<FormRecordDto>(`/patientmr/${patientId}/forms/${formId}/finalize`, {});
    if (res.success && res.data) {
      return res.data;
    }
    throw new Error(res.error?.message || 'Failed to finalize form');
  }

  static async reopen(patientId: string, formId: string, dto: ReopenFormDto): Promise<FormRecordDto> {
    const res = await apiClient.put<FormRecordDto>(`/patientmr/${patientId}/forms/${formId}/reopen`, dto);
    if (res.success && res.data) {
      return res.data;
    }
    throw new Error(res.error?.message || 'Failed to reopen form');
  }

  static async approvalReceived(patientId: string, formId: string, notes?: string): Promise<FormRecordDto> {
    const res = await apiClient.put<FormRecordDto>(`/patientmr/${patientId}/forms/${formId}/approvalreceived`, { notes });
    if (res.success && res.data) {
      return res.data;
    }
    throw new Error(res.error?.message || 'Failed to record approval');
  }

  static async expire(patientId: string, formId: string): Promise<FormRecordDto> {
    const res = await apiClient.put<FormRecordDto>(`/patientmr/${patientId}/forms/${formId}/expire`, {});
    if (res.success && res.data) {
      return res.data;
    }
    throw new Error(res.error?.message || 'Failed to expire form');
  }

  /**
   * Phase 19 F10/F14: fill the form's blank fields from the chart (care plan, assessment, meds,
   * diagnoses, agency profile). Typed values are kept unless overwrite is true.
   * F23: F00139 (TX_THERAPIES_PA) fills from one therapy discipline's chart, sent as `discipline`.
   */
  static async fillFromChart(
    patientId: string,
    formId: string,
    data: Record<string, unknown>,
    overwrite = false,
    discipline?: TherapyDiscipline
  ): Promise<FormRecordDto & { prefillSources?: Record<string, string>; prefillMissing?: Array<{ label: string }> }> {
    const res = await apiClient.put<FormRecordDto & { prefillSources?: Record<string, string>; prefillMissing?: Array<{ label: string }> }>(
      `/patientmr/${patientId}/forms/${formId}`,
      { data, prefill: true, overwrite, ...(discipline ? { discipline } : {}) }
    );
    if (res.success && res.data) return res.data;
    throw new Error(res.error?.message || 'Failed to fill the form from the chart');
  }

  static async generatePdf(patientId: string, formId: string): Promise<{ pdf: string; contentType: string; filename: string }> {
    const res = await apiClient.post<{ pdf: string; contentType: string; filename: string }>(
      `/patientmr/${patientId}/forms/${formId}/pdf`
    );
    if (res.success && res.data) {
      return res.data;
    }
    throw new Error(res.error?.message || 'Failed to generate PDF');
  }

  static async addDocument(patientId: string, formId: string, file: File): Promise<FormRecordDto> {
    const documentData = await toBase64(file);
    const res = await apiClient.post<{ success: boolean; document: unknown; form: FormRecordDto }>(
      `/patientmr/${patientId}/forms/${formId}/documents`,
      { documentData, documentName: file.name, contentType: file.type }
    );
    if (res.success && res.data?.form) return res.data.form;
    throw new Error(res.error?.message || 'Failed to upload document');
  }

  static async getDocumentUrl(patientId: string, formId: string, documentId: string): Promise<{ url: string; name: string }> {
    const res = await apiClient.get<{ url: string; name: string }>(
      `/patientmr/${patientId}/forms/${formId}/documents/${documentId}`
    );
    if (res.success && res.data) {
      return res.data;
    }
    throw new Error(res.error?.message || 'Failed to get document URL');
  }

  static async deleteDocument(patientId: string, formId: string, documentId: string): Promise<FormRecordDto> {
    const res = await apiClient.delete<{ success: boolean; form: FormRecordDto }>(
      `/patientmr/${patientId}/forms/${formId}/documents/${documentId}`
    );
    if (res.success && res.data?.form) return res.data.form;
    throw new Error(res.error?.message || 'Failed to delete document');
  }
}
